import { useQuery } from "@tanstack/react-query";

import { axiosInstance, PRICES } from "api";

import { usePricesDateStr } from "./prices";

export interface ForecastSlot {
  time: string; // "HH:mm"
  p10: number;
  p50: number;
  p90: number;
  probNegative: number | null;
}

export interface TargetForecast {
  issuedAt: string;
  modelVersion: string;
  slots: ForecastSlot[];
  // For slots that have already begun: the forecast that stood when each one
  // started, so it can be compared with what PSE then published.
  history: ForecastSlot[];
}

export interface PriceForecastResponse {
  date: string;
  // Day-ahead (RDN/SDAC) forecast covering the requested day, or null once
  // nothing has been issued for it.
  da: TargetForecast | null;
  // CEN nowcast: the slot in progress and the next two hours; only today has one.
  cen: TargetForecast | null;
}

export const useGetPriceForecast = (date?: Date) => {
  const dateStr = usePricesDateStr(date);
  return useQuery<PriceForecastResponse>({
    queryKey: [PRICES.FORECAST, dateStr],
    queryFn: async () => {
      const { data } = await axiosInstance.get<PriceForecastResponse>(
        PRICES.FORECAST,
        { params: { date: dateStr } },
      );
      return data;
    },
    // The nowcast is re-issued every 15 minutes; poll like the prices do.
    refetchInterval: 60_000,
    staleTime: 30_000,
    refetchOnWindowFocus: true,
    refetchOnReconnect: true,
  });
};

export interface ForecastScore {
  slots: number;
  mae: number;
  baselineMae: number | null;
  skill: number | null;
  coverage80: number;
}

export interface ForecastScoreWindows {
  last7: ForecastScore | null;
  last30: ForecastScore | null;
}

export interface PriceForecastScoreResponse {
  da: ForecastScoreWindows;
  cen: ForecastScoreWindows;
}

export const useGetPriceForecastScore = () =>
  useQuery<PriceForecastScoreResponse>({
    queryKey: [PRICES.FORECAST_SCORE],
    queryFn: async () => {
      const { data } = await axiosInstance.get<PriceForecastScoreResponse>(
        PRICES.FORECAST_SCORE,
      );
      return data;
    },
    staleTime: 5 * 60 * 1000,
  });
